import axios from 'axios';
import { toast } from 'react-toastify'; // Import Toast
import { API_BASE_URL } from './urls.js';

// 1. Setup a variable to hold the loader reference
let loaderRef = null;

// This function allows App.js to pass the loader instance here
export const setLoaderRef = (ref) => {
    loaderRef = ref;
};

const axiosClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

/**
 * Request Interceptor: Attach JWT & Start Loader
 */
axiosClient.interceptors.request.use(
    (config) => {
        // Start the Top Bar Loader
        if (loaderRef) loaderRef.continuousStart();

        // Existing JWT Logic
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        // Stop loader if request fails to start
        if (loaderRef) loaderRef.complete();
        return Promise.reject(error);
    }
);

/**
 * Response Interceptor: Handle Loader, Alerts, and Global Errors
 */
axiosClient.interceptors.response.use(
    (response) => {
        // 1. Stop the Loader
        if (loaderRef) loaderRef.complete();

        // 2. Success Alert Logic (Optional but recommended)
        // We usually only show success alerts for actions (POST/PUT/DELETE), not fetches (GET).
        if (['post', 'put', 'delete', 'patch'].includes(response.config.method)) {

            const requestUrl = response.config.url || "";
            const isSilentRequest = requestUrl.includes("/onboarding/events") || requestUrl.includes("/onboarding/track");

            if (!isSilentRequest) {
                const successMessage = response.data?.message || 'Operation successful';
                toast.success(successMessage);
            }

            // const successMessage = response.data?.message || 'Operation successful';
            // toast.success(successMessage);
        }

        return response;
    },
    (error) => {
        // 1. Stop the Loader
        if (loaderRef) loaderRef.complete();

        // 2. Extract error details
        const { response } = error;
        const status = response?.status;
        const errorCode = response?.data?.code;
        const errorMessage = response?.data?.message || "An unexpected error occurred.";

        // 3. Trigger Global Alert
        // Note: You might want to suppress this for 401s if the redirect is enough
        if (status !== 401) { 
            toast.error(errorMessage);
        }

        // 4. Handle Token Expiration (401)
        if (status === 401 && (errorCode === 'TOKEN_EXPIRED' || errorCode === 'INVALID_TOKEN')) {
            console.warn("Authentication required or token expired. Redirecting to login.");
            
            // Optional: Show a specific session expired toast
            toast.info("Session expired. Please login again.");

            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('stripeAccountId');
            
            window.location.href = '/login?session_expired=true';
            return Promise.reject(error);
        }

        // 5. Handle Subscription Canceled (403)
        if (status === 403 && errorCode === 'SUBSCRIPTION_CANCELED') {
            console.warn("Organization subscription canceled. Redirecting to billing page.");
            
            toast.warning("Subscription canceled. Redirecting to billing...");

            setTimeout(() => {
                window.location.href = '/subscriptions/manage'; 
            }, 2000);
            return Promise.reject(error);
        }

        // For all other errors, reject so the component can handle local logic if needed
        return Promise.reject(error);
    }
);

export default axiosClient;